import { useState, useEffect } from "react";
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    Typography,
} from "@mui/material";
import { LogEntry as LogEntryType, LogsResponse } from "../../interface/returnOps";
import LogFilters from "../molecules/LogFilters";
import LogEntry from "../molecules/LogEntry";

interface BackendLogsProps {
    logs: LogsResponse | null;
    loading: boolean;
    onRefresh: () => void;
}

const BackendLogs = ({ logs, loading, onRefresh }: BackendLogsProps) => {
    const [logLevelFilter, setLogLevelFilter] = useState<"all" | "info" | "debug" | "warn" | "error">("all");
    const [autoRefreshLogs, setAutoRefreshLogs] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [hideDebug, setHideDebug] = useState(false);

    useEffect(() => {
        if (autoRefreshLogs) {
            const interval = setInterval(() => {
                onRefresh();
            }, 5000);
            return () => clearInterval(interval);
        }
        return undefined;
    }, [autoRefreshLogs, onRefresh]);

    const filteredLogs = logs?.logs.filter((log: LogEntryType) => {
        // Filter by log level
        if (logLevelFilter !== "all" && log.level !== logLevelFilter) {
            return false;
        }

        // Filter out debug logs if hideDebug is enabled
        if (hideDebug && log.level === "debug") {
            return false;
        }

        // Filter by search query
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            // Check message
            if (log.message.toLowerCase().includes(query)) {
                return true;
            }
            // Check correlationId in metadata
            if (log.metadata) {
                // Check if correlationId exists and matches
                const correlationId = log.metadata.correlationId || log.metadata.CorrelationId;
                if (correlationId && String(correlationId).toLowerCase().includes(query)) {
                    return true;
                }
                // Check all metadata values
                const metadataMatches = Object.values(log.metadata).some(
                    (value) => String(value).toLowerCase().includes(query),
                );
                if (metadataMatches) {
                    return true;
                }
            }
            return false;
        }

        return true;
    }).reverse() || [];

    return (
        <Card>
            <CardHeader
                title="Backend Logs"
                action={(
                    <LogFilters
                        searchQuery={searchQuery}
                        onSearchChange={setSearchQuery}
                        logLevelFilter={logLevelFilter}
                        onLogLevelChange={setLogLevelFilter}
                        hideDebug={hideDebug}
                        onHideDebugChange={setHideDebug}
                        autoRefresh={autoRefreshLogs}
                        onAutoRefreshChange={setAutoRefreshLogs}
                        onRefresh={onRefresh}
                        loading={loading}
                    />
                )}
            />
            <CardContent>
                {loading ? (
                    <CircularProgress />
                ) : (
                    <Box
                        sx={{
                            maxHeight: 400,
                            overflowY: "auto",
                            backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f5f5f5'),
                            borderRadius: 1,
                            p: 2,
                        }}
                    >
                        {filteredLogs.length > 0 ? (
                            filteredLogs.map((log, index) => (
                                <LogEntry
                                    key={index}
                                    log={log}
                                    showDivider={index < filteredLogs.length - 1}
                                />
                            ))
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No logs available
                            </Typography>
                        )}
                    </Box>
                )}
                {logs && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                        Total logs: {logs.count} | Last updated: {new Date(logs.timestamp).toLocaleString()}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

export default BackendLogs;
